import './PacingBar.scss';
import cn from 'classnames';
import {
  ReactElement,
  ReactNode,
  useEffect,
  useId,
  useLayoutEffect,
  useRef,
  useState
} from 'react';
import { createPortal } from 'react-dom';
import {
  Bar,
  BarChart,
  Cell,
  LabelList,
  LabelProps,
  ReferenceLine,
  ResponsiveContainer
} from 'recharts';
import { ChartXAxis, ChartYAxis } from '@Components/Charts/chart-presets';
import { Img } from '@Components/Img';
import { GoalPacingColors } from '@src/utils/colors';
import { measureTextWidth } from '@src/utils/canvasTextUtils';
import {
  BAR_SIZE_BY_LEVEL,
  COMPACT_LEVEL,
  DEFAULT_BAR_SIZE,
  END_LABEL_ALLOWANCE,
  GOAL_LABEL_FONT,
  GOAL_LABEL_GAP,
  LABEL_INSET,
  LABEL_Y_OFFSETS,
  MODAL_BAR_NUDGE,
  MODAL_LABEL_INSET,
  MODAL_RIGHT_GUTTER,
  TOTAL_BAR_SIZE,
  Y_LABEL_EXTRA_WIDTH
} from '../pacing-constants';
import {
  calculatePositionOfBudgetLine,
  formatGoalLabel,
  formatPacingBarValue,
  GOAL_VALUE_MAP_FOR_BAR_LABELS,
  isPacingRatioGoalType,
  isTotalBar,
  scalePacingBarWidth
} from '../pacingUtils';
import {
  formatYAxisTickLabel,
  formatYValue,
  layerLabelFits,
  normalizePacingBar,
  placeCursorOverlay
} from './pacing-bar-utils';

// Labels are sized from canvas text measurements, which use a fallback font until the web fonts
// load. Bars mounted before then re-render once they have, so those measurements are taken again
// with the real font; bars mounted after never need to.
let fontsAreReady = typeof document === 'undefined' || !document.fonts;
const fontsReady = fontsAreReady
  ? Promise.resolve()
  : document.fonts.ready.then(() => {
      fontsAreReady = true;
    });

const useFontsReady = () => {
  const [, setReady] = useState(fontsAreReady);
  useEffect(() => {
    if (fontsAreReady) {
      return undefined;
    }
    let cancelled = false;
    fontsReady.then(() => {
      if (!cancelled) {
        setReady(true);
      }
    });
    return () => {
      cancelled = true;
    };
  }, []);
};

const keys = ['totalCleared', 'totalProjected', 'totalRemainder'];

interface CustomYAxisTickProps {
  // recharts injects x/y/payload when this is used as a YAxis `tick` element.
  x?: number;
  y?: number;
  payload?: { value: string };
  marginLeft: number;
  isModal: boolean;
  numberOfSegments: number;
  longestText: number;
  total: boolean;
  currentSegment?: string;
}

const CustomYAxisTick = ({
  x = 0,
  y = 0,
  payload = { value: '' },
  marginLeft,
  isModal,
  numberOfSegments,
  longestText,
  total,
  currentSegment = ''
}: CustomYAxisTickProps) => {
  if (isModal) {
    return (
      <foreignObject x={x - 48} y={y - 12} width={200} height={100}>
        <div className='pacing-bar-text'>Total</div>
      </foreignObject>
    );
  }

  const newValue = formatYValue(payload.value, marginLeft, numberOfSegments);
  const segmentsWithLogos = ['Channel', 'Platform'];

  return (
    <foreignObject
      x={x - longestText - Y_LABEL_EXTRA_WIDTH}
      y={marginLeft === 1 ? y - 16 : y - 12}
      // Size the label column to the longest measured label (+ logo/gap allowance) so long values
      // aren't clipped.
      width={longestText + Y_LABEL_EXTRA_WIDTH}
      height={124}
    >
      <div className='pacing-bar-y-axis-container'>
        <div className='pacing-bar-logos'>
          {segmentsWithLogos.includes(currentSegment) &&
            !total &&
            !(marginLeft === 1 && numberOfSegments > 1) && (
              <Img
                src={`https://cdn.blisspointmedia.com/assets/img/${currentSegment.toLowerCase()}s/${payload.value.replace(
                  /\s/g,
                  ''
                )}.png`}
                alt='brand health header'
                className='logo-image'
              />
            )}
        </div>
        <div className={`pacing-bar-text${marginLeft}`}>
          <div>{formatYAxisTickLabel(total, newValue)}</div>
        </div>
      </div>
    </foreignObject>
  );
};

const bluesReversed = [
  GoalPacingColors.remainder,
  GoalPacingColors.projected,
  GoalPacingColors.delivered
];

interface PacingBarInfo {
  totalCleared: number;
  totalProjected: number;
  totalRemainder: number;
  segmentName: string;
  budget: number;
}

interface GoalPacingBarProps {
  pacingData: PacingBarInfo;
  colors?: string[];
  marginLeft: number;
  numberOfSegments: number;
  longestText: number;
  isActual: boolean;
  isModal?: boolean;
  currentSegment?: string;
  maxGoal?: number;
  goalName: string;
}

interface CustomBarWithTargetProps {
  // recharts injects the bar rectangle geometry when this is used as a Bar `shape` element.
  fill?: string;
  x?: number;
  y?: number;
  height?: number;
  width?: number;
  barKeyIndex: number;
  targetValue: number;
  isModal: boolean;
  barKeys: string[];
  budgetLinePosition: ReturnType<typeof calculatePositionOfBudgetLine>;
  goalName: string;
}

const CustomBarWithTarget = ({
  fill,
  x: propX = 0,
  y = 0,
  height = 0,
  width: rawWidth = 0,
  barKeyIndex,
  targetValue,
  isModal,
  barKeys,
  budgetLinePosition,
  goalName
}: CustomBarWithTargetProps): ReactElement => {
  const width = rawWidth < 0 ? 1 : rawWidth;

  const goalText = formatGoalLabel(targetValue, goalName);

  const currentBar = barKeys[barKeyIndex];
  const { positionOfBudgetLine, xValue } = budgetLinePosition;
  const calculatedXPosition = xValue * width;

  const x = isModal ? propX - MODAL_BAR_NUDGE : propX;
  return (
    <svg>
      <rect x={x} y={y} width={width} height={height} stroke='none' fill={fill} />
      {/* Only draw the goal line where a goal is actually set. A goalless bar (the Total, or a
          ratio ancestor the goal cascades below) carries budget 0, so it renders no target — a
          ratio goal never blends up to a level it wasn't set at. */}
      {targetValue > 0 && currentBar === positionOfBudgetLine && (
        <>
          <line
            x1={x + calculatedXPosition}
            x2={x + calculatedXPosition}
            y1={y}
            y2={y + height}
            stroke={GoalPacingColors.budget}
            strokeWidth={3}
          />
          <text
            className='pacing-bar-goal-label'
            x={x + calculatedXPosition + GOAL_LABEL_GAP}
            y={y + height / 2}
            dominantBaseline='middle'
          >
            {goalText}
          </text>
        </>
      )}
    </svg>
  );
};

const renderCustomizedLabel = (
  props: { x: number; y: number },
  marginLeft: number,
  isModal: boolean,
  isActual: boolean,
  goalValue: number,
  key: string,
  pacingData: PacingBarInfo,
  total: boolean,
  goalName: string
): ReactElement | null => {
  let valueTag = '';
  // Derived-metric projected labels intentionally carry null (formatted as 0 downstream).
  let value: number | null = 0;
  const { x, y } = props;
  if (key === 'totalRemainder') {
    return null;
  }
  const isProjected = key === 'totalProjected';
  if (isProjected) {
    [, valueTag] = GOAL_VALUE_MAP_FOR_BAR_LABELS[goalName];
    value = isPacingRatioGoalType(goalName)
      ? null
      : pacingData.totalProjected + pacingData.totalCleared;
  } else if (key === 'totalCleared') {
    [valueTag] = GOAL_VALUE_MAP_FOR_BAR_LABELS[goalName];
    value = pacingData.totalCleared;
  }

  const formattedValue = formatPacingBarValue(
    value ?? 0,
    valueTag,
    goalValue,
    isModal,
    isActual,
    goalName
  );

  let yOffsets: readonly [number, number] = LABEL_Y_OFFSETS.default;
  if (marginLeft === 3 || marginLeft === 4) {
    yOffsets = LABEL_Y_OFFSETS[marginLeft];
  } else if (total && !isModal) {
    yOffsets = LABEL_Y_OFFSETS.total;
  }

  return (
    <text
      className={cn('pacing-bar-value', { compact: marginLeft >= COMPACT_LEVEL })}
      x={x + (isModal ? MODAL_LABEL_INSET : LABEL_INSET)}
      y={y + yOffsets[isProjected ? 1 : 0]}
      textAnchor='start'
      dominantBaseline={marginLeft === 4 ? 'auto' : 'hanging'}
    >
      {formattedValue}
    </text>
  );
};

const GoalPacingBar = ({
  pacingData,
  colors = bluesReversed,
  marginLeft,
  longestText,
  numberOfSegments,
  isModal = false,
  isActual,
  currentSegment,
  maxGoal,
  goalName
}: GoalPacingBarProps) => {
  useFontsReady();
  const { budget: currentGoal } = pacingData;

  const total = marginLeft === 0;
  const maxTextWidth = measureTextWidth(pacingData.segmentName, "1rem 'Geist'");
  // The goal label starts at the budget line and can run past the bar end, so the right gutter
  // must hold all of it or the SVG clips it.
  const goalLabelWidth =
    currentGoal > 0
      ? measureTextWidth(formatGoalLabel(currentGoal, goalName), GOAL_LABEL_FONT) + GOAL_LABEL_GAP
      : 0;
  const { container: containerHeight, bar: barHeight } =
    BAR_SIZE_BY_LEVEL[marginLeft] ??
    (isTotalBar(total, marginLeft) && !isModal ? TOTAL_BAR_SIZE : DEFAULT_BAR_SIZE);

  const budgetLinePosition = calculatePositionOfBudgetLine(pacingData);
  const editedBarWidget = scalePacingBarWidth(currentGoal, maxGoal || 0, marginLeft);
  const isDerived = isPacingRatioGoalType(goalName);
  const normalizedPacingData = [normalizePacingBar(pacingData, isDerived)];

  return (
    <ResponsiveContainer
      width={`${isModal ? 100 : editedBarWidget}%`}
      height={containerHeight}
      debounce={50}
    >
      <BarChart
        reverseStackOrder
        data={normalizedPacingData}
        layout='vertical'
        margin={{
          left: isModal ? 0 : longestText,
          right: isModal
            ? MODAL_RIGHT_GUTTER
            : Math.max(maxTextWidth + END_LABEL_ALLOWANCE, goalLabelWidth)
        }}
        {...{
          overflow: 'visible'
        }}
      >
        <ChartXAxis hide type='number' domain={[0, 1]} />
        <ChartYAxis
          dataKey='segmentName'
          type='category'
          tick={
            <CustomYAxisTick
              marginLeft={marginLeft}
              numberOfSegments={numberOfSegments}
              longestText={longestText}
              isModal={isModal}
              currentSegment={currentSegment}
              total={total}
            />
          }
          tickFormatter={value => formatYValue(value, marginLeft, numberOfSegments)}
        />
        {keys
          .slice()
          .reverse()
          .map((key, index) => (
            <Bar
              key={key}
              dataKey={key}
              stackId='a'
              barSize={barHeight}
              isAnimationActive={false}
              label={props =>
                renderCustomizedLabel(
                  props as { x: number; y: number },
                  marginLeft,
                  isModal,
                  isActual,
                  currentGoal,
                  key,
                  pacingData,
                  isTotalBar(total, marginLeft),
                  goalName
                )
              }
              shape={
                <CustomBarWithTarget
                  barKeyIndex={index}
                  targetValue={currentGoal}
                  isModal={isModal}
                  barKeys={keys}
                  budgetLinePosition={budgetLinePosition}
                  goalName={goalName}
                />
              }
            >
              <Cell fill={colors[index]} />
            </Bar>
          ))}
      </BarChart>
    </ResponsiveContainer>
  );
};

// One stacked segment of a layered bar; `value` is the segment's width in the same units as `max`.
export interface PacingBarLayer {
  key: string;
  value: number;
  color: string;
  label?: ReactNode;
}

// A vertical target line drawn across the bar at `value`.
export interface PacingBarMarker {
  key: string;
  value: number;
  color: string;
}

interface LayeredPacingBarProps {
  layers: PacingBarLayer[];
  markers?: PacingBarMarker[];
  // Shared by sibling bars so they scale against each other.
  max: number;
  // Shown after the bar. Pass `false` to keep the slot empty, so sibling rows stay the same width.
  endLabel?: ReactNode;
  // Shown before the bar. Pass `false` to keep the slot empty, so sibling rows stay aligned.
  alert?: ReactNode;
  // Popover content shown on hover or keyboard focus, normally a ChartTooltipV2.
  overlay?: ReactNode;
  // Text for screen readers; the overlay is not available to them. Required with `overlay`.
  ariaLabel?: string;
}

export const LAYER_HEIGHT = 40;
const MARKER_WIDTH = 6;
export const MARKER_OVERHANG = 4;

const LAYER_LABEL_FONT = "500 14px 'Geist'";
const LAYER_LABEL_PADDING = 12;

// A label that is wider than its segment would overlap its neighbors and clip, so it is left out
// there; the hover overlay still carries every value.
const renderLayerLabel = (
  content: ReactNode,
  { x = 0, y = 0, width = 0, height = 0 }: LabelProps
) => {
  const labelWidth = typeof content === 'string' ? measureTextWidth(content, LAYER_LABEL_FONT) : 0;
  if (!layerLabelFits(Number(width), labelWidth, LAYER_LABEL_PADDING)) {
    return null;
  }
  return (
    <foreignObject
      x={Number(x)}
      y={Number(y)}
      width={Math.max(Number(width), 0)}
      height={Number(height)}
    >
      <div className='pacing-bar-layer-label'>{content}</div>
    </foreignObject>
  );
};

const CURSOR_OFFSET = 12;

interface CursorOverlayProps {
  overlay: ReactNode;
  ariaLabel?: string;
  children: ReactElement;
}

// Shows `overlay` next to the mouse while it is over `children`, or beside `children` while they
// have keyboard focus, flipping to the other side when it would run off the screen.
const CursorOverlay = ({ overlay, ariaLabel, children }: CursorOverlayProps) => {
  const overlayId = useId();
  const [cursor, setCursor] = useState<{ x: number; y: number }>();
  const [size, setSize] = useState({ width: 0, height: 0 });
  const [isFocused, setIsFocused] = useState(false);
  const overlayRef = useRef<HTMLDivElement>(null);
  const isHovering = cursor !== undefined;

  // A focus-anchored overlay is fixed to the viewport, so it would be left behind by a scroll.
  useEffect(() => {
    if (!isFocused) {
      return undefined;
    }
    const clear = () => setCursor(undefined);
    window.addEventListener('scroll', clear, true);
    return () => window.removeEventListener('scroll', clear, true);
  }, [isFocused]);

  useLayoutEffect(() => {
    const rect = overlayRef.current?.getBoundingClientRect();
    if (rect) {
      setSize({ width: rect.width, height: rect.height });
    }
  }, [overlay, isHovering]);

  const position = cursor
    ? placeCursorOverlay(
        cursor,
        size,
        { width: window.innerWidth, height: window.innerHeight },
        CURSOR_OFFSET
      )
    : { x: 0, y: 0 };

  return (
    // The bar is a read-only graphic, but keyboard users must be able to reach its overlay.
    /* eslint-disable jsx-a11y/no-noninteractive-element-interactions, jsx-a11y/no-noninteractive-tabindex */
    <div
      className='pacing-bar-hover'
      role='img'
      tabIndex={0}
      aria-label={ariaLabel}
      aria-describedby={isHovering ? overlayId : undefined}
      onMouseMove={e => setCursor({ x: e.clientX, y: e.clientY })}
      onMouseLeave={() => setCursor(undefined)}
      onFocus={e => {
        setIsFocused(true);
        const rect = e.currentTarget.getBoundingClientRect();
        setCursor({ x: rect.left + rect.width / 2, y: rect.top + rect.height / 2 });
      }}
      onBlur={() => {
        setIsFocused(false);
        setCursor(undefined);
      }}
      onKeyDown={e => {
        if (e.key === 'Escape') {
          setCursor(undefined);
        }
      }}
    >
      {children}
      {cursor &&
        createPortal(
          <div
            ref={overlayRef}
            id={overlayId}
            role='tooltip'
            className='pacing-bar-cursor-overlay'
            style={{ left: position.x, top: position.y }}
          >
            {overlay}
          </div>,
          document.body
        )}
    </div>
    /* eslint-enable jsx-a11y/no-noninteractive-element-interactions, jsx-a11y/no-noninteractive-tabindex */
  );
};

// Generic stacked bar for pages with their own delivery layers (Linear, Streaming): segments
// drawn left to right on a [0, max] scale, plus target markers, an end label, an alert and a
// hover overlay.
const LayeredPacingBar = ({
  layers,
  markers = [],
  max,
  endLabel,
  alert,
  overlay,
  ariaLabel
}: LayeredPacingBarProps) => {
  useFontsReady();
  const datum = Object.fromEntries(layers.map(({ key, value }) => [key, value]));
  const bar = (
    <div className='pacing-bar-layered'>
      {alert !== undefined && <div className='pacing-bar-alert'>{alert}</div>}
      <div className='pacing-bar-chart'>
        <ResponsiveContainer width='100%' height={LAYER_HEIGHT + 2 * MARKER_OVERHANG}>
          <BarChart
            data={[datum]}
            layout='vertical'
            margin={{ top: 0, right: 0, bottom: 0, left: 0 }}
            {...{
              overflow: 'visible'
            }}
          >
            <ChartXAxis hide type='number' domain={[0, max || 1]} allowDataOverflow />
            <ChartYAxis hide type='category' />
            {layers.map(({ key, color, label }) => (
              <Bar
                key={key}
                dataKey={key}
                stackId='layers'
                fill={color}
                barSize={LAYER_HEIGHT}
                isAnimationActive={false}
              >
                {label && <LabelList content={props => renderLayerLabel(label, props)} />}
              </Bar>
            ))}
            {markers.map(({ key, value, color }) => (
              <ReferenceLine key={key} x={value} stroke={color} strokeWidth={MARKER_WIDTH} />
            ))}
          </BarChart>
        </ResponsiveContainer>
      </div>
      {endLabel !== undefined && <span className='pacing-bar-end-label'>{endLabel}</span>}
    </div>
  );

  return overlay ? (
    <CursorOverlay overlay={overlay} ariaLabel={ariaLabel}>
      {bar}
    </CursorOverlay>
  ) : (
    bar
  );
};

type PacingBarProps = GoalPacingBarProps | LayeredPacingBarProps;

// Cross-channel callers pass goal data (budget line, goal labels); pages with their own delivery
// layers pass `layers` and `markers` instead.
export const PacingBar = (props: PacingBarProps) =>
  'layers' in props ? <LayeredPacingBar {...props} /> : <GoalPacingBar {...props} />;
